import type { Subject, AgeBand } from '../types'

export interface GameDef {
  id: string
  subject: Subject
  title: string
  titleEn: string
  emoji: string
  description: string
  descriptionEn: string
  ageBands: AgeBand[]
}

export const GAMES: GameDef[] = [
  // ── Mathematics ──────────────────────────────────────────────────────────────
  {
    id: 'pizza-slicer', subject: 'mathematics', emoji: '🍕',
    title: 'Pizzaskjærer', titleEn: 'Pizza Slicer',
    description: 'Trykk på skiver for å vise riktig brøkdel', descriptionEn: 'Tap slices to show the correct fraction',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'balance-scale', subject: 'mathematics', emoji: '⚖️',
    title: 'Vektskål', titleEn: 'Balance Scale',
    description: 'Finn det manglende tallet for å balansere vekten', descriptionEn: 'Find the missing number to balance the scale',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'number-line-racer', subject: 'mathematics', emoji: '🎯',
    title: 'Tallinjejakt', titleEn: 'Number Line Racer',
    description: 'Dra markøren til riktig posisjon på tallinja', descriptionEn: 'Drag the marker to the correct position',
    ageBands: ['4-7', '8-12', '13-16'],
  },

  // ── Science ──────────────────────────────────────────────────────────────────
  {
    id: 'food-chain-builder', subject: 'science', emoji: '🌿',
    title: 'Næringskjede', titleEn: 'Food Chain Builder',
    description: 'Sett organismer i riktig rekkefølge', descriptionEn: 'Arrange organisms in the correct order',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'particle-lab', subject: 'science', emoji: '🔬',
    title: 'Partikkellab', titleEn: 'Particle Lab',
    description: 'Juster temperaturen og observer aggregattilstander', descriptionEn: 'Adjust temperature and observe states of matter',
    ageBands: ['8-12', '13-16'],
  },
  {
    id: 'life-cycle-sorter', subject: 'science', emoji: '🦋',
    title: 'Livssyklus', titleEn: 'Life Cycle Sorter',
    description: 'Sorter livssyklusen i riktig rekkefølge', descriptionEn: 'Sort the life cycle stages in order',
    ageBands: ['4-7', '8-12', '13-16'],
  },

  // ── Technology ───────────────────────────────────────────────────────────────
  {
    id: 'code-sequencer', subject: 'technology', emoji: '🤖',
    title: 'Kodesekvenser', titleEn: 'Code Sequencer',
    description: 'Bygg en sekvens for å hjelpe roboten til målet', descriptionEn: 'Build a sequence to guide the robot to the goal',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'binary-flip', subject: 'technology', emoji: '💻',
    title: 'Binærflipper', titleEn: 'Binary Flip',
    description: 'Flip biter til riktig desimaltall', descriptionEn: 'Flip bits to match the decimal number',
    ageBands: ['8-12', '13-16'],
  },
  {
    id: 'bug-hunt', subject: 'technology', emoji: '🐛',
    title: 'Feilsøking', titleEn: 'Bug Hunt',
    description: 'Finn og rett feilen i kodesekvensen', descriptionEn: 'Find and fix the bug in the code sequence',
    ageBands: ['8-12', '13-16'],
  },

  // ── Engineering ──────────────────────────────────────────────────────────────
  {
    id: 'bridge-builder', subject: 'engineering', emoji: '🌉',
    title: 'Brobygger', titleEn: 'Bridge Builder',
    description: 'Velg riktig brokonstruksjon for hver last', descriptionEn: 'Choose the right bridge design for each load',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'gear-chain', subject: 'engineering', emoji: '⚙️',
    title: 'Tannhjulkjede', titleEn: 'Gear Chain',
    description: 'Forutsig retning og hastighet på tannhjulene', descriptionEn: 'Predict gear direction and speed',
    ageBands: ['8-12', '13-16'],
  },
  {
    id: 'ramp-runner', subject: 'engineering', emoji: '⛷️',
    title: 'Rampeløper', titleEn: 'Ramp Runner',
    description: 'Juster vinkelen for å lande ballen i bøtten', descriptionEn: 'Adjust the angle to land the ball in the bucket',
    ageBands: ['8-12', '13-16'],
  },

  // ── Arts ─────────────────────────────────────────────────────────────────────
  {
    id: 'colour-mixer', subject: 'arts', emoji: '🎨',
    title: 'Fargemikseren', titleEn: 'Colour Mixer',
    description: 'Bland primærfarger for å lage nye farger', descriptionEn: 'Mix primary colours to create new ones',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'symmetry-painter', subject: 'arts', emoji: '🪞',
    title: 'Symmetrimaler', titleEn: 'Symmetry Painter',
    description: 'Mål og skap symmetriske mønstre', descriptionEn: 'Create symmetric patterns with a mirror line',
    ageBands: ['4-7', '8-12', '13-16'],
  },
  {
    id: 'rhythm-tapper', subject: 'arts', emoji: '🥁',
    title: 'Rytmetapper', titleEn: 'Rhythm Tapper',
    description: 'Gjenskap rytmemønsteret', descriptionEn: 'Copy the rhythm pattern',
    ageBands: ['4-7', '8-12', '13-16'],
  },
]

export function getGameById(id: string): GameDef | undefined {
  return GAMES.find(g => g.id === id)
}

export function getGamesBySubject(subject: Subject): GameDef[] {
  return GAMES.filter(g => g.subject === subject)
}
